<template>
  <nav class="app-topnav navbar navbar-expand-lg w-100">
    <div class="container-fluid app-topnav-inner">
      <router-link class="app-topnav-brand" to="/home" aria-label="Progress IMS">
        <img class="app-topnav-brand-logo" :src="logo" alt="Progress IMS" />
        <span class="app-topnav-brand-divider" aria-hidden="true"></span>
        <span class="app-topnav-brand-copy">
          <span class="app-topnav-brand-sub">Business Account</span>
        </span>
      </router-link>

      <button
        class="navbar-toggler app-topnav-toggler"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#appTopnav"
        aria-controls="appTopnav"
        aria-expanded="false"
        :aria-label="$t('navMenu')"
      >
        <span class="app-topnav-toggler-lines" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      <div id="appTopnav" class="collapse navbar-collapse">
        <div class="app-topnav-rail">
          <ul class="navbar-nav app-topnav-links">
            <li
              v-for="item in navItems"
              :key="item.key"
              class="nav-item"
              :class="{ 'is-active': isActive(item.key), 'is-disabled': item.disabled }"
            >
              <router-link
                v-if="!item.disabled"
                :to="item.to"
                class="nav-link"
                @click="closeMobileMenu"
              >
                <i :class="item.icon" class="app-topnav-link-icon" aria-hidden="true"></i>
                <span class="app-topnav-link-label">{{ $t(item.labelKey) }}</span>
              </router-link>
              <span
                v-else
                class="nav-link app-topnav-link-disabled"
                :title="$t('navSelectPolicyFirst')"
              >
                <i :class="item.icon" class="app-topnav-link-icon" aria-hidden="true"></i>
                <span class="app-topnav-link-label">{{ $t(item.labelKey) }}</span>
              </span>
            </li>
          </ul>
        </div>

        <div class="app-topnav-actions">
          <div v-if="policyId" class="app-topnav-policy">
            <span class="app-topnav-policy-dot" aria-hidden="true"></span>
            <span class="app-topnav-policy-text">
              <span class="app-topnav-policy-kicker">{{ $t("navPolicyLabel") }}</span>
              <span class="app-topnav-policy-id">{{ policyNumberLabel }}</span>
            </span>
          </div>

          <button
            type="button"
            class="app-theme-toggle"
            :aria-label="isDarkTheme ? $t('navThemeToLight') : $t('navThemeToDark')"
            :title="isDarkTheme ? $t('navThemeToLight') : $t('navThemeToDark')"
            @click="onToggleTheme"
          >
            <i :class="isDarkTheme ? 'bi bi-sun' : 'bi bi-moon'" aria-hidden="true"></i>
          </button>

          <div class="dropdown app-topnav-lang">
            <button
              class="app-topnav-lang-btn"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <img :src="currentLanguageFlag" alt="" class="app-topnav-lang-flag-img" aria-hidden="true" />
              <span class="app-topnav-lang-code">{{ currentLocale.toUpperCase() }}</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end app-topnav-lang-menu">
              <li v-for="lang in languages" :key="lang.code">
                <button
                  type="button"
                  class="dropdown-item d-flex align-items-center gap-2"
                  :class="{ active: currentLocale === lang.code }"
                  @click="setLanguage(lang.code)"
                >
                  <img :src="lang.flag" alt="" class="app-topnav-lang-item-flag" aria-hidden="true" />
                  <span>{{ $t(lang.nameKey) }}</span>
                </button>
              </li>
            </ul>
          </div>

          <div class="dropdown app-topnav-user">
            <button
              class="app-topnav-user-btn"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span class="app-topnav-user-avatar" aria-hidden="true">{{ userInitials }}</span>
              <span class="app-topnav-user-meta d-none d-lg-flex">
                <span class="app-topnav-user-name">{{ userName }}</span>
                <span v-if="companyName" class="app-topnav-user-company">{{ companyName }}</span>
              </span>
              <i class="fas fa-chevron-down app-topnav-user-caret" aria-hidden="true"></i>
            </button>

            <ul class="dropdown-menu dropdown-menu-end app-topnav-dropdown">
              <li class="app-topnav-dropdown-head">
                <div class="app-topnav-dropdown-profile">
                  <span class="app-topnav-dropdown-avatar">{{ userInitials }}</span>
                  <span class="app-topnav-dropdown-meta">
                    <strong>{{ userName }}</strong>
                    <small v-if="companyName">{{ companyName }}</small>
                  </span>
                </div>
              </li>

              <li class="app-topnav-dropdown-section">
                <router-link class="dropdown-item" :to="{ path: '/home' }" @click="closeMobileMenu">
                  <span class="app-topnav-dropdown-icon" aria-hidden="true">
                    <i class="fas fa-cog"></i>
                  </span>
                  <span class="app-topnav-dropdown-copy">
                    <span class="app-topnav-dropdown-label">{{ $t("navSettings") }}</span>
                    <span class="app-topnav-dropdown-hint">{{ $t("navSettingsHint") }}</span>
                  </span>
                </router-link>
              </li>

              <li class="app-topnav-dropdown-section">
                <router-link
                  class="dropdown-item"
                  :to="{ path: '/home', query: { tab: 'password' } }"
                  @click="closeMobileMenu"
                >
                  <span class="app-topnav-dropdown-icon" aria-hidden="true">
                    <i class="fas fa-key"></i>
                  </span>
                  <span class="app-topnav-dropdown-copy">
                    <span class="app-topnav-dropdown-label">{{ $t("navChangePassword") }}</span>
                    <span class="app-topnav-dropdown-hint">{{ $t("navChangePasswordHint") }}</span>
                  </span>
                </router-link>
              </li>

              <li class="app-topnav-dropdown-footer">
                <button type="button" class="dropdown-item is-danger" @click="signOut">
                  <span class="app-topnav-dropdown-icon is-danger" aria-hidden="true">
                    <i class="fas fa-sign-out-alt"></i>
                  </span>
                  <span class="app-topnav-dropdown-copy">
                    <span class="app-topnav-dropdown-label">{{ $t("navLogout") }}</span>
                    <span class="app-topnav-dropdown-hint">{{ $t("navLogoutHint") }}</span>
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import CryptoJS from "crypto-js";
import { getBrandLogo, weblink } from "../../project/Source/helper";
import { changeLanguage } from "../../project/Source/lang/i18n";
import { getTheme, toggleTheme } from "../../project/Source/theme";
import azFlag from "@/assets/img/icons/flags/az.svg";
import enFlag from "@/assets/img/icons/flags/en.svg";
import ruFlag from "@/assets/img/icons/flags/ru.svg";
import trFlag from "@/assets/img/icons/flags/tr.svg";
import {
  filesUrl,
  getPolicyId,
  getRouteSection,
  groupClinicUrl,
  policyUrl,
  requestUrl,
} from "../../project/Source/routes";

export default {
  name: "Navbar",
  data() {
    return {
      logo: getBrandLogo(),
      theme: getTheme(),
      languages: [
        { code: "az", nameKey: "languageAz", flag: azFlag },
        { code: "en", nameKey: "languageEn", flag: enFlag },
        { code: "ru", nameKey: "languageRu", flag: ruFlag },
        { code: "tr", nameKey: "languageTr", flag: trFlag },
      ],
    };
  },
  computed: {
    isDarkTheme() {
      return this.theme === "dark";
    },
    currentLocale() {
      return this.$i18n.locale || "az";
    },
    currentLanguageFlag() {
      const lang = this.languages.find((item) => item.code === this.currentLocale);
      return lang ? lang.flag : azFlag;
    },
    policyId() {
      const fromRoute = getPolicyId(this.$route);
      if (fromRoute) {
        return fromRoute;
      }

      try {
        const stored = sessionStorage.getItem("policy");
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    },
    policyNumberLabel() {
      try {
        const stored = sessionStorage.getItem("policyNumber");
        if (stored) {
          const number = JSON.parse(stored);
          if (number && number !== "none") {
            return number;
          }
        }
      } catch {
        // ignore parse errors
      }

      return this.policyId ? String(this.policyId) : "";
    },
    user() {
      try {
        return JSON.parse(
          CryptoJS.AES.decrypt(
            window.localStorage.getItem("user"),
            "secret_key"
          ).toString(CryptoJS.enc.Utf8)
        );
      } catch {
        return {};
      }
    },
    userName() {
      return this.user.fullName || this.$t("navAccount");
    },
    companyName() {
      return this.user.companyName || "";
    },
    userInitials() {
      const name = (this.userName || "").trim();
      if (!name || name === this.$t("navAccount")) {
        return "P";
      }

      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }

      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    },
    navItems() {
      const policyId = this.policyId;

      return [
        {
          key: "home",
          labelKey: "navHome",
          icon: "fas fa-home",
          to: "/home",
          disabled: false,
        },
        {
          key: "policy",
          labelKey: "navPolicy",
          icon: "fas fa-file-alt",
          to: policyId ? policyUrl(policyId) : null,
          disabled: !policyId,
        },
        {
          key: "group-clinic",
          labelKey: "navClinics",
          icon: "fas fa-hospital",
          to: policyId ? groupClinicUrl(policyId) : null,
          disabled: !policyId,
        },
        {
          key: "request",
          labelKey: "navRequests",
          icon: "fas fa-inbox",
          to: policyId ? requestUrl(policyId) : null,
          disabled: !policyId,
        },
        {
          key: "files",
          labelKey: "navFiles",
          icon: "fas fa-folder",
          to: policyId ? filesUrl(policyId) : null,
          disabled: !policyId,
        },
      ];
    },
  },
  methods: {
    onToggleTheme() {
      this.theme = toggleTheme();
    },
    setLanguage(code) {
      changeLanguage(code);
      this.closeMobileMenu();
    },
    currentSection() {
      return getRouteSection(this.$route);
    },
    isActive(key) {
      const section = this.currentSection();

      if (key === "home") {
        return section === "home" || section === "";
      }

      return section === key;
    },
    closeMobileMenu() {
      const collapse = document.getElementById("appTopnav");
      if (!collapse || !collapse.classList.contains("show")) {
        return;
      }

      collapse.classList.remove("show");
    },
    signOut() {
      window.localStorage.setItem(
        "jwt",
        CryptoJS.AES.encrypt(JSON.stringify("none"), "secret_key").toString()
      );
      window.localStorage.setItem(
        "roles",
        CryptoJS.AES.encrypt(JSON.stringify([{ id: null }]), "secret_key").toString()
      );
      window.localStorage.setItem(
        "user",
        CryptoJS.AES.encrypt(JSON.stringify([{ id: null }]), "secret_key").toString()
      );
      window.location.href = `${weblink}signIn`;
    },
  },
};
</script>
